/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { ShaderMaterial, Vector3 } from 'three';
import { PointsMaterialShape, CIRCLE_SHADER, RHOMBUS_SHADER, TRIANGLE_SHADER, CROSS_SHADER, SPHERE_SHADER } from '@sick-davinci/glow';
/**
 * which attribute is used for coloring the pointcloud
 */
export var CrownPointsMaterialColorAttribute;
(function (CrownPointsMaterialColorAttribute) {
    CrownPointsMaterialColorAttribute["PRELIMARY_COLOR"] = "preliminaryColor";
    CrownPointsMaterialColorAttribute["X"] = "position[0]";
    CrownPointsMaterialColorAttribute["Y"] = "position[1]";
    CrownPointsMaterialColorAttribute["Z"] = "position[2]";
})(CrownPointsMaterialColorAttribute || (CrownPointsMaterialColorAttribute = {}));
/**
 * Which color interpolation should be uses
 */
export var CrownPoinsMaterialColorMode;
(function (CrownPoinsMaterialColorMode) {
    /**
     * no interpolation: just use preliminaryColor from buffer attribute
     */
    CrownPoinsMaterialColorMode["STATIC"] = "preliminaryColor";
    /**
     * interpolate discrete
     */
    CrownPoinsMaterialColorMode["DISCRETE_INTERPOLATION"] = "stops[stopLower]";
    /**
     * interpolate linear
     */
    CrownPoinsMaterialColorMode["LINEAR_INTERPOLATION"] = "mix(stops[stopLower], stops[stopUpper], vec3(weighted, weighted, weighted))";
})(CrownPoinsMaterialColorMode || (CrownPoinsMaterialColorMode = {}));
/**
 * A material used by PointsClouds and Scans
 */
export class CrownPointsMaterial extends ShaderMaterial {
    constructor(parameters) {
        super({
            vertexShader: CrownPointsMaterial._getVertexShader(parameters),
            fragmentShader: CrownPointsMaterial._getFragmentShader(parameters),
            uniforms: {
                size: { value: parameters && parameters.size || 1.0 },
                valueRange: { type: 'f', value: 1 },
                valueOffset: { type: 'f', value: 0 },
                stopValuesSize: { type: 'i', value: 0 },
                stops: { type: 'v3v', value: new Array() },
                stopValues: { type: 'fv1', value: new Array() },
            },
        });
        // initalize stops with default values
        for (let i = 0; i < CrownPointsMaterial.MAX_STOPS; i++) {
            this.uniforms.stops.value[i] = new Vector3();
            this.uniforms.stopValues.value[i] = 0.0;
        }
    }
    static _getVertexShader(parameters) {
        const usePolarDataBuffers = parameters && parameters.usePolarDataBuffers || false;
        const interpolationMode = parameters && parameters.interpolationMode || CrownPoinsMaterialColorMode.STATIC;
        const sizeAttenuation = parameters && parameters.sizeAttenuation || false;
        const preliminaryColorAttribute = parameters && parameters.colorAttribute || CrownPointsMaterialColorAttribute.PRELIMARY_COLOR;
        const useInterpolation = interpolationMode !== CrownPoinsMaterialColorMode.STATIC;
        const interpolationCode = useInterpolation ? `
    int stopLower = 0;
    int stopUpper = 1;

    float value = ${preliminaryColorAttribute};
    float normalizedValue = value / valueRange;

    for (int i = 1; i < MAX_STOPS; i++) {
      if (i >= stopValuesSize) {
        break;
      }
      if (normalizedValue > stopValues[i]) {
        stopUpper = i + 1;
        stopLower = i;
      }
    }

    float stopValueRange = stopValues[stopUpper] - stopValues[stopLower];
    float weighted = (normalizedValue - stopValues[stopLower]) / stopValueRange;
    ` : '';
        const vertexShader = `
    #define MAX_STOPS ${CrownPointsMaterial.MAX_STOPS}

    ${usePolarDataBuffers ? this.POLARBUFFER_ATTRIBUTES : ''}
    ${useInterpolation ? this.COLOR_INTERPOLATION_ATTRIBUTES : this.COLOR_STATIC_ATTRIBUTES}

    uniform float size;

    varying vec3 vColor;

    void main()
    {
      ${interpolationCode}

      vColor = ${interpolationMode};

      ${usePolarDataBuffers ? this.CALC_POSITION_POLAR : this.CALC_POSITION_CARTESIAN}
      gl_Position = projectionMatrix * mvPosition;

      float scale = 300.0;
      gl_PointSize = ${sizeAttenuation ? 'size * ( scale / - mvPosition.z )' : 'size'};
    }`;
        return vertexShader;
    }
    static _getFragmentShader(parameters) {
        const shape = parameters && parameters.pointsShape || PointsMaterialShape.circle;
        switch (shape) {
            case PointsMaterialShape.circle:
                return CIRCLE_SHADER.fragmentShader;
            case PointsMaterialShape.rhombus:
                return RHOMBUS_SHADER.fragmentShader;
            case PointsMaterialShape.triangle:
                return TRIANGLE_SHADER.fragmentShader;
            case PointsMaterialShape.cross:
                return CROSS_SHADER.fragmentShader;
            case PointsMaterialShape.sphere:
                return SPHERE_SHADER.fragmentShader;
            default:
                return CIRCLE_SHADER.fragmentShader;
        }
    }
    setColorMap(colorMap, min, max) {
        this.uniforms.valueRange.value = max - min;
        this.uniforms.valueOffset.value = Math.abs(min);
        this.uniforms.stopValuesSize.value = colorMap.length;
        colorMap.forEach((stop, i) => {
            this.uniforms.stops.value[i].fromArray(stop.rgb);
            this.uniforms.stopValues.value[i] = stop.p;
        });
        // for a more efficient extracting of colors out of the colormap
        // prepare the map that there are always at least 3 elements inside e.g. stops with values: [0.2 0.8 INFINITY]
        // this makes it easier to interpolate the correct color inside the shader see shader code
        if (colorMap.length === 0) {
            this.uniforms.stops.value[0].fromArray(CrownPointsMaterial.DEFAULT_COLOR);
            this.uniforms.stopValues.value[0] = 0.0;
            this.uniforms.stops.value[1].fromArray(CrownPointsMaterial.DEFAULT_COLOR);
            this.uniforms.stopValues.value[1] = 1.0;
            this.uniforms.stopValuesSize.value = 2;
        }
        if (colorMap.length === 1) {
            this.uniforms.stops.value[1].fromArray(this.uniforms.stops.value[0].toArray());
            if (this.uniforms.stopValues.value[0] < 1.0) {
                this.uniforms.stopValues.value[1] = 1.0;
            }
            else {
                this.uniforms.stopValues.value[1] = this.uniforms.stopValues.value[0];
                this.uniforms.stopValues.value[0] = 0;
            }
            this.uniforms.stopValuesSize.value = 2;
        }
        this.uniforms.stopValues.value[this.uniforms.stopValuesSize.value] = Number.MAX_VALUE;
        this.uniforms.stops.value[this.uniforms.stopValuesSize.value]
            .fromArray(this.uniforms.stops.value[this.uniforms.stopValuesSize.value - 1].toArray());
        this.uniforms.stopValuesSize.value++;
        return this;
    }
}
CrownPointsMaterial.DEFAULT_COLOR = [0, 0, 0];
CrownPointsMaterial.MAX_STOPS = 20;
CrownPointsMaterial.POLARBUFFER_ATTRIBUTES = `attribute float phi;
     attribute float theta;
     attribute float dist;`;
CrownPointsMaterial.COLOR_INTERPOLATION_ATTRIBUTES = `attribute float preliminaryColor;
     uniform vec3 stops[MAX_STOPS];
     uniform float stopValues[MAX_STOPS];
     uniform int stopValuesSize;
     uniform float valueRange;`;
CrownPointsMaterial.COLOR_STATIC_ATTRIBUTES = `attribute vec3 preliminaryColor;`;
CrownPointsMaterial.CALC_POSITION_CARTESIAN = 'vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );';
CrownPointsMaterial.CALC_POSITION_POLAR = 'vec4 mvPosition = modelViewMatrix * vec4(cos(theta) * cos(phi) * dist, sin(theta) * cos(phi) * dist, -sin(phi) * dist, 1.0);';
//# sourceMappingURL=CrownPointsMaterial.js.map